import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthService } from '@coin/shared/auth/data-access';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { environment } from '@coin/shared/core/util-environments';
import {
  ImporterMetadata,
  ImportTableFile,
  MassExportTransactionStatus,
  S3File,
  TransactionStatus
} from '@coin/shared/core/util-models';
import { ToastrService } from 'ngx-toastr';
import { from, Observable } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { ImporterName } from '../../interfaces/enums/importer-name.enum';

@Injectable()
export class ImporterBaseService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastrService);
  private readonly loading = inject(LoadingService);
  private readonly httpHelpersService = inject(HttpHelpersService);
  private readonly loadingService = inject(LoadingService);
  private readonly authService = inject(AuthService);

  public upload(
    file: File,
    importerName: ImporterName,
    params?: HttpParams,
    withTransaction = false,
    uploadSubPath = 'upload'
  ): Observable<void> {
    this.loading.present();
    return this.http
      .get<{ signedUrl: string }>(
        `${environment.api.documentsUrl}/importer/${importerName}/${uploadSubPath}/${encodeURIComponent(file.name)}`,
        {
          params
        }
      )
      .pipe(
        switchMap(({ signedUrl }) => this.http.put<void>(signedUrl, file)),
        tap(() => {
          if (withTransaction) this.toast.info('Starting import...');
          else this.toast.success('Uploaded file');
        }),
        this.httpHelpersService.handleError('Error trying to upload file'),
        finalize(() => this.loading.dismiss())
      );
  }

  public getUploadList(importerName: string): Observable<S3File<ImporterMetadata>[]> {
    return this.http.get<S3File<ImporterMetadata>[]>(
      `${environment.api.documentsUrl}/importer/${importerName}/list/info`
    );
  }

  public download(filename: string, importerName: string): Observable<unknown> {
    const params = new HttpParams().append('file', encodeURIComponent(filename));

    this.loading.present();
    return this.http
      .get<{ signedUrl: string }>(`${environment.api.documentsUrl}/importer/${importerName}/download`, { params })
      .pipe(
        tap(({ signedUrl }) => window.open(signedUrl, '_blank')),
        this.httpHelpersService.handleError('Error trying to download file'),
        finalize(() => this.loading.dismiss())
      );
  }

  public massDownloadKeyExperts(importerName: string): Observable<TransactionStatus<MassExportTransactionStatus>> {
    return this.http.get<TransactionStatus<MassExportTransactionStatus>>(
      `${environment.api.baseUrl}/admin/api/v3/master/employees/experts/export`,
      {
        params: new HttpParams().set('fileName', importerName)
      }
    );
  }

  public delete(filename: string, importerName: string): Observable<boolean> {
    this.loading.present();
    return this.http
      .delete<boolean>(
        `${environment.api.documentsUrl}/importer/${importerName}/delete/${encodeURIComponent(filename)}`
      )
      .pipe(
        this.httpHelpersService.handleError('Error trying to delete file'),
        finalize(() => this.loading.dismiss())
      );
  }

  public deleteEmployeeDataFile(
    file: ImportTableFile,
    importerName: ImporterName
  ): Observable<{ transactionId: string }> {
    this.loadingService.present();
    return from(this.authService.getActiveUser()).pipe(
      map(user => ({
        fileName: `${importerName}/converted/${file.name.replace('.xlsx', '.csv')}`,
        type: 'EmployeeCompensationOverlay',
        deletedByGid: user.gid
      })),
      switchMap(body =>
        this.http.delete<{ transactionId: string }>(
          `${environment.api.baseUrl}/admin/api/v1/master/importer/compensation-overlay`,
          { body }
        )
      ),
      tap(() => this.toast.success('Started deletion.')),
      this.httpHelpersService.handleError('Cannot delete employee-data'),
      finalize(() => this.loadingService.dismiss())
    );
  }

  public massDownloadBenchmarks(importerName: string): Observable<TransactionStatus<MassExportTransactionStatus>> {
    this.loadingService.present();

    return this.http
      .post<TransactionStatus<MassExportTransactionStatus>>(
        `${environment.api.baseUrl}/admin/api/v3/master/employees/benchmarks/export`,
        {
          params: new HttpParams().set('fileName', importerName)
        }
      )
      .pipe(finalize(() => this.loadingService.dismiss()));
  }

  public massDownloadBenchmarksAdvanced(): Observable<TransactionStatus<MassExportTransactionStatus>> {
    this.loadingService.present();

    return this.http
      .post<
        TransactionStatus<MassExportTransactionStatus>
      >(`${environment.api.baseUrl}/admin/api/v3/master/employees/benchmarks-advanced/export`, {})
      .pipe(finalize(() => this.loadingService.dismiss()));
  }
}
